import * as React from 'react';
import {
  AgnesAlert,
  AgnesButton,
  AgnesCheckbox,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
} from '@agnes/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { SearchInput } from '@/components/SearchInput';
import { GRAUS_RISCO } from '@/constants/ConstantGrauRiscoRubrica';
import { ITEM_PAGE_SIZES } from '@/constants/ConstantRubricaLotePagination';
import { SITUACOES_RUBRICA } from '@/constants/ConstantSituacaoRubrica';
import { STATUS_ANALISE } from '@/constants/ConstantStatusAnaliseRubrica';
import ServiceRubricaLote, { type RubricaLoteFilters } from '@/services/ServiceRubricaLote';

import FilterRubricaAnaliseLoteRegistros from './FilterRubricaAnaliseLoteRegistros';
import {
  type AppliedFilters,
  EMPTY_FILTERS,
  requestFilters,
} from '@/components/rubricas/rubricaLoteFilters';
import { Pagina } from '@/types/paginacao/Pagina';
import { Rubrica } from '@/types/rubricas/Rubrica';
import { RubricaAnaliseLote } from '@/types/rubricas/analise-lote/RubricaAnaliseLote';
import { BaseComponentProps } from '@/components/BaseComponentProps';
import { BadgeGrauExposicaoRisco } from '@/components/analise/BadgeGrauExposicaoRisco';
import { BadgeRubricaSituacao } from '@/components/rubricas/situacao/BadgeRubricaSituacao';
import { BadgeStatusAnalise } from '@/components/analise/BadgeStatusAnalise';

function formatCompetence(value: string | null): string {
  if (value?.length === 7 && /^\d{4}-(0[1-9]|1[0-2])$/.test(value)) {
    return `${value.slice(5)}/${value.slice(0, 4)}`;
  }
  return value || '-';
}

export interface TableRubricasAnaliseLoteRegistrosProps extends BaseComponentProps {
  lote: RubricaAnaliseLote;
  baseFilters: RubricaLoteFilters;
  /** Seleção controlada pelo pai (barra de ação em massa); sem ela, a tabela controla a própria. */
  selected?: ReadonlyMap<number, Rubrica>;
  onSelectedChange?: (selected: Map<number, Rubrica>) => void;
}

export default function TableRubricasAnaliseLoteRegistros({
  lote,
  baseFilters,
  dataQa,
  selected: controlledSelected,
  onSelectedChange,
}: TableRubricasAnaliseLoteRegistrosProps) {
  const [searchInput, setSearchInput] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [filters, setFilters] = React.useState<AppliedFilters>(EMPTY_FILTERS);
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(50);
  const [retry, setRetry] = React.useState(0);
  const [result, setResult] = React.useState<Pagina<Rubrica> | null>(null);
  const [error, setError] = React.useState(false);
  const [internalSelected, setInternalSelected] = React.useState<ReadonlyMap<number, Rubrica>>(
    new Map(),
  );
  const selected = controlledSelected ?? internalSelected;
  // Ref para o efeito de carga limpar a seleção sem depender da identidade do callback.
  const onSelectedChangeRef = React.useRef(onSelectedChange);
  onSelectedChangeRef.current = onSelectedChange;
  const setSelected = React.useCallback((next: Map<number, Rubrica>) => {
    if (onSelectedChangeRef.current) onSelectedChangeRef.current(next);
    else setInternalSelected(next);
  }, []);

  React.useEffect(() => {
    let ignore = false;
    setResult(null);
    setError(false);
    ServiceRubricaLote.consultarItens({
      grupo: lote.grupo,
      baseFilters,
      itemFilters: requestFilters(filters, search),
      page,
      size,
    })
      .then(({ data }) => {
        if (ignore) return;
        if (page > 0 && page >= data.totalPages) {
          setSelected(new Map());
          setPage(Math.max(0, data.totalPages - 1));
          return;
        }
        setResult(data);
      })
      .catch(() => {
        if (!ignore) setError(true);
      });
    return () => {
      ignore = true;
    };
  }, [baseFilters, filters, lote.grupo, page, retry, search, setSelected, size]);

  const allSelected =
    !!result?.content.length && result.content.every((item) => selected.has(item.idAnaliseRubrica));
  const someSelected = result?.content.some((item) => selected.has(item.idAnaliseRubrica));

  return (
    <div data-qa={dataQa} className="flex flex-col items-stretch justify-start gap-4">
      <div data-qa={`${dataQa}-ferramentas`} className="flex flex-wrap items-center gap-3">
        <div data-qa={`${dataQa}-busca`} className="min-w-0 basis-64 flex-1">
          <SearchInput
            value={searchInput}
            onValueChange={setSearchInput}
            onSearch={(value) => {
              setSearch(value.trim());
              setPage(0);
              setSelected(new Map());
            }}
            placeholder="Pesquisar nos itens deste grupo"
          />
        </div>
        <FilterRubricaAnaliseLoteRegistros
          dataQa={`${dataQa}-filtro`}
          grupo={lote.grupo}
          baseFilters={baseFilters}
          filters={filters}
          onApply={(value) => {
            setFilters(value);
            setPage(0);
            setSelected(new Map());
          }}
        />
      </div>
      <div
        data-qa={`${dataQa}-selecao`}
        className="flex flex-wrap items-center justify-between gap-2 text-sm"
      >
        <span data-qa={`${dataQa}-selecao-total`} role="status" className="text-agnes-text-muted">
          {selected.size} itens selecionados neste grupo
        </span>
        {selected.size > 0 && (
          <AgnesButton
            data-qa={`${dataQa}-selecao-limpar`}
            variant="text"
            onClick={() => setSelected(new Map())}
          >
            Limpar seleção
          </AgnesButton>
        )}
      </div>
      {error ? (
        <AgnesAlert data-qa={`${dataQa}-erro`} preset="error">
          Não foi possível carregar os itens deste grupo.
          <AgnesButton
            data-qa={`${dataQa}-tentar-novamente`}
            variant="text"
            onClick={() => {
              setSelected(new Map());
              setRetry((value) => value + 1);
            }}
          >
            Tentar novamente
          </AgnesButton>
        </AgnesAlert>
      ) : !result ? (
        <p
          data-qa={`${dataQa}-carregando`}
          role="status"
          className="p-6 text-center text-sm text-agnes-text-muted"
        >
          Carregando itens do grupo...
        </p>
      ) : result.content.length === 0 ? (
        <p
          data-qa={`${dataQa}-vazio`}
          className="rounded-lg border border-dashed border-agnes-border p-6 text-center text-sm text-agnes-text-muted"
        >
          Nenhuma rubrica encontrada neste grupo para os filtros informados.
        </p>
      ) : (
        <>
          <div
            data-qa={`${dataQa}-tabela-container`}
            className="max-w-full overflow-x-auto rounded-lg border border-agnes-border"
            role="region"
            aria-label="Tabela de itens do grupo"
            tabIndex={0}
          >
            <table
              data-qa={`${dataQa}-tabela`}
              className="w-full min-w-[1800px] bg-[var(--background)] text-left text-sm text-agnes-text-tables"
            >
              <caption data-qa={`${dataQa}-tabela-legenda`} className="sr-only">
                Itens de {lote.descricao}, página {page + 1}
              </caption>
              <thead
                data-qa={`${dataQa}-tabela-cabecalho`}
                className="bg-agnes-bg-table-header text-xs text-agnes-text-secondary"
              >
                <tr data-qa={`${dataQa}-tabela-cabecalho-linha`}>
                  <th data-qa={`${dataQa}-col-selecao`} scope="col" className="p-3">
                    <AgnesCheckbox
                      data-qa={`${dataQa}-selecao-pagina`}
                      aria-label="Selecionar itens desta página"
                      checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                      onCheckedChange={() => {
                        const next = new Map(selected);
                        result.content.forEach((item) => {
                          if (allSelected) next.delete(item.idAnaliseRubrica);
                          else next.set(item.idAnaliseRubrica, item);
                        });
                        setSelected(next);
                      }}
                    />
                  </th>
                  {[
                    { key: 'codigoRubrica', label: 'Código' },
                    { key: 'naturezaCodigo', label: 'Natureza' },
                    { key: 'naturezaNome', label: 'Nome da natureza' },
                    { key: 'idTabela', label: 'Id. da tabela' },
                    { key: 'inicioValidade', label: 'Início validade' },
                    { key: 'fimValidade', label: 'Fim validade' },
                    { key: 'cp', label: 'CP' },
                    { key: 'irrf', label: 'IRRF' },
                    { key: 'fgts', label: 'FGTS' },
                    { key: 'cprp', label: 'CPRP' },
                    { key: 'pis', label: 'PIS' },
                    { key: 'grauRisco', label: 'Grau de risco' },
                    { key: 'ocorrencia', label: 'Ocorrência' },
                    { key: 'situacao', label: 'Situação' },
                    { key: 'statusAnalise', label: 'Status' },
                  ].map(({ key, label }) => (
                    <th
                      data-qa={`${dataQa}-col-${key}`}
                      key={key}
                      scope="col"
                      className="whitespace-nowrap p-3 font-semibold"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody data-qa={`${dataQa}-tabela-corpo`} className="divide-y divide-agnes-border">
                {result.content.map((item) => {
                  const itemQa = `${dataQa}-item-${item.idAnaliseRubrica}`;
                  const risk = GRAUS_RISCO.find((option) => option.value === item.grauRisco);
                  const situation = SITUACOES_RUBRICA.find(
                    (option) => option.value === item.situacao,
                  );
                  const status = STATUS_ANALISE.find(
                    (option) => option.value === item.statusAnalise,
                  );
                  return (
                    <tr
                      data-qa={itemQa}
                      key={item.idAnaliseRubrica}
                      className={`align-middle ${selected.has(item.idAnaliseRubrica) ? 'bg-agnes-primary/5' : ''}`}
                    >
                      <td data-qa={`${itemQa}-cell-selecao`} className="p-3">
                        <AgnesCheckbox
                          data-qa={`${itemQa}-selecao`}
                          aria-label={`Selecionar análise ${item.idAnaliseRubrica}`}
                          checked={selected.has(item.idAnaliseRubrica)}
                          onCheckedChange={() => {
                            const next = new Map(selected);
                            if (next.has(item.idAnaliseRubrica)) next.delete(item.idAnaliseRubrica);
                            else next.set(item.idAnaliseRubrica, item);
                            setSelected(next);
                          }}
                        />
                      </td>
                      <td
                        data-qa={`${itemQa}-cell-codigoRubrica`}
                        className="max-w-64 break-words p-3 font-medium"
                      >
                        {item.codigoRubrica || '-'}
                      </td>
                      <td data-qa={`${itemQa}-cell-naturezaCodigo`} className="p-3">
                        {item.naturezaCodigo ?? '-'}
                      </td>
                      <td
                        data-qa={`${itemQa}-cell-naturezaNome`}
                        className="max-w-64 break-words p-3"
                      >
                        {item.naturezaNome || '-'}
                      </td>
                      <td data-qa={`${itemQa}-cell-idTabela`} className="p-3">
                        {item.idTabela || '-'}
                      </td>
                      <td data-qa={`${itemQa}-cell-inicioValidade`} className="p-3">
                        {formatCompetence(item.inicioValidade)}
                      </td>
                      <td data-qa={`${itemQa}-cell-fimValidade`} className="p-3">
                        {formatCompetence(item.fimValidade)}
                      </td>
                      <td data-qa={`${itemQa}-cell-cp`} className="p-3">
                        {item.cp || '-'}
                      </td>
                      <td data-qa={`${itemQa}-cell-irrf`} className="p-3">
                        {item.irrf || '-'}
                      </td>
                      <td data-qa={`${itemQa}-cell-fgts`} className="p-3">
                        {item.fgts || '-'}
                      </td>
                      <td data-qa={`${itemQa}-cell-cprp`} className="p-3">
                        {item.cprp || '-'}
                      </td>
                      <td data-qa={`${itemQa}-cell-pis`} className="p-3">
                        {item.pis || '-'}
                      </td>
                      <td data-qa={`${itemQa}-cell-grauRisco`} className="p-3">
                        {risk ? (
                          <BadgeGrauExposicaoRisco grau={risk.value} />
                        ) : (
                          item.grauRisco || '-'
                        )}
                      </td>
                      <td
                        data-qa={`${itemQa}-cell-ocorrencia`}
                        className="w-32 p-3 text-agnes-text-muted"
                      >
                        <div
                          data-qa={`${itemQa}-ocorrencia-texto`}
                          className="w-24 whitespace-normal break-words text-xs leading-5"
                        >
                          {item.ocorrencia
                            ? `${item.ocorrencia.codigo}${item.ocorrencia.nome ? ` - ${item.ocorrencia.nome}` : ''}`
                            : '-'}
                        </div>
                      </td>
                      <td data-qa={`${itemQa}-cell-situacao`} className="p-3">
                        {situation ? (
                          <BadgeRubricaSituacao situacao={situation.value} />
                        ) : (
                          item.situacao || '-'
                        )}
                      </td>
                      <td data-qa={`${itemQa}-cell-statusAnalise`} className="p-3">
                        {status ? (
                          <BadgeStatusAnalise status={status.value} />
                        ) : (
                          item.statusAnalise || '-'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div
            data-qa={`${dataQa}-paginacao`}
            className="flex flex-wrap items-center justify-between gap-3"
          >
            <span data-qa={`${dataQa}-paginacao-total`} className="text-sm text-agnes-text-muted">
              {result.totalElements.toLocaleString('pt-BR')} itens no grupo filtrado
            </span>
            <div
              data-qa={`${dataQa}-paginacao-controles`}
              className="flex flex-wrap items-center gap-2"
            >
              <AgnesSelect
                value={String(size)}
                onValueChange={(value) => {
                  setSize(Number(value));
                  setPage(0);
                }}
              >
                <AgnesSelectTrigger
                  data-qa={`${dataQa}-paginacao-tamanho-trigger`}
                  className="w-24"
                  aria-label="Itens por página do grupo"
                >
                  <AgnesSelectValue data-qa={`${dataQa}-paginacao-tamanho-valor`} />
                </AgnesSelectTrigger>
                <AgnesSelectContent data-qa={`${dataQa}-paginacao-tamanho-popover`}>
                  {ITEM_PAGE_SIZES.map((option) => (
                    <AgnesSelectItem
                      data-qa={`${dataQa}-paginacao-tamanho-opcao-${option}`}
                      key={option}
                      value={String(option)}
                    >
                      {option}
                    </AgnesSelectItem>
                  ))}
                </AgnesSelectContent>
              </AgnesSelect>
              <span
                data-qa={`${dataQa}-paginacao-pagina`}
                className="text-sm text-agnes-text-muted"
              >
                Página de itens {page + 1} de {result.totalPages}
              </span>
              <AgnesButton
                data-qa={`${dataQa}-paginacao-anterior`}
                variant="outlined"
                aria-label="Página anterior de itens"
                disabled={page === 0}
                onClick={() => setPage((value) => value - 1)}
              >
                <ChevronLeft data-qa={`${dataQa}-paginacao-anterior-icone`} className="h-4 w-4" />
              </AgnesButton>
              <AgnesButton
                data-qa={`${dataQa}-paginacao-proxima`}
                variant="outlined"
                aria-label="Próxima página de itens"
                disabled={page + 1 >= result.totalPages}
                onClick={() => setPage((value) => value + 1)}
              >
                <ChevronRight data-qa={`${dataQa}-paginacao-proxima-icone`} className="h-4 w-4" />
              </AgnesButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
